// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Lock,
  Unlock,
  AlertTriangle,
  Settings,
  Users,
  BarChart3,
  FileText,
  MessageSquare,
  Clock,
  Briefcase,
  ChevronRight,
  X,
  Loader2,
  Plus,
  FileSearch,
} from 'lucide-react';
import InternalChatbot from '../../components/common/InternalChatbot';
import { useAbilityContext } from '../../contexts/AbilityContext';
import { keycloakService } from '../../services/keycloakService';
import {
  CREDIT_BLOCK_OPS_HOME_MODE_STORAGE_KEY,
  readCreditBlockOpsHomeMode,
  CreditBlockMyWorkOverview,
  CreditBlockOverviewAssigneeTraversalPrefetch,
  CreditBlockTeamOverview,
  CreditBlockUnassignedCaseQueue,
} from '../../components/credit-block/CreditBlockOperationsDashboardPanels';
import CreditReportOrderWidget from '../../components/common/CreditReportOrderWidget';
import { CreditBlockQuickAddClientModal } from '../../components/credit-block/CreditBlockQuickAddClientModal';
import { creditReportService } from '../../services/creditReportService';
import {
  useCreditReportHydrationQueries,
  useCreditReportsPaginated,
} from '../../services/queries/creditReportQueries';
import type { CreditReportOrder } from '../../types';
import { CreditBlockNewCaseModal } from '../../components/credit-block/CreditBlockNewCaseModal';
import CreditBlockClientDetail from './CreditBlockClientDetail.refactored';
import { CREDIT_BLOCK_CLIENT_DEFAULT_SECTION, isKnownCreditBlockClientSection } from './creditBlockClientDetailUrl';
import CaseDetailView from '../../components/credit-block/CaseDetailView';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { gobodoRecordKeys } from '../../services/queries/gobodoRecordQueries';
import { updateRecord } from '../../services/recordStorageService';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import { convertFromGobodoRecord } from '../../services/gobodo/gobodoRecordService';
import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getCustomSuffix as getCreditBlockSuffix } from '../../config/gobodoConfig';
import type { CreditBlockClient, CreditBlockProduct } from '../../types';
import type { ListViewRecord } from '../../types/listView';
import {
  useCreditBlockSubscriptionsPaginated,
  useCreditBlockClientsPaginated,
  useCreditBlockClientsExactCount,
  useCreditBlockClientsJoinQueries,
  useRecentlyViewedCreditBlockClients,
  mergeJoinIntoClient,
  CREDIT_BLOCK_BULK_LIST_SIZE,
  fetchCreditBlockClientByLookup,
} from '../../services/queries/creditBlockQueries';
import { mergeClientsByPickId } from '../../services/creditBlock/creditBlockClientPick';
import {
  useCreditBlockCasesPaginated,
  useCreditBlockCaseLinkedClientQueries,
  DEFAULT_CASES_PAGE_SIZE,
  toClientGobodoObjectKey,
} from '../../services/queries/creditBlockCaseQueries';
import {
  useCreditBlockUnlockRequests,
  type UnlockRequestSortOrder,
  type UnlockRequestWithClient,
} from '../../services/queries/creditBlockHistoryQueries';
import { getBureauLogo } from '../../constants/bureauLogos';
import { resolveGraphUserNamesBySfIds, looksLikeGraphUserSfId } from '../../services/gobodo/gobodoUserSfIdLookup';
import { GobodoRequiredGuard } from '../../components/common/GobodoRequiredGuard';
import { useApplicationContext } from '../../contexts/ApplicationContext';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { StandardSearchBar, StandardSelect, StandardPagination, StandardButton } from '../../components/standard';
import { CreditBlockListHeader } from '../../components/credit-block/CreditBlockListHeader';

function isLegacyClientProduct(p: CreditBlockProduct): boolean {
  return String(p.id).startsWith('prod-');
}

/** Real CreditBlock__c rows merged from join (not Client picklist placeholders). */
function creditBlockSubscriptions(products: CreditBlockProduct[] | undefined): CreditBlockProduct[] {
  return (products ?? []).filter((p) => !isLegacyClientProduct(p));
}

/** When we have merged CreditBlock rows, status reflects those — not Client.SubscriptionStatus__c alone. */
function listSubscriptionStatus(client: CreditBlockClient): CreditBlockClient['subscriptionStatus'] {
  const cb = creditBlockSubscriptions(client.assignedProducts);
  if (cb.length === 0) return client.subscriptionStatus;
  if (cb.some((p) => p.status === 'active')) return 'active';
  if (cb.some((p) => p.status === 'suspended')) return 'suspended';
  if (cb.every((p) => p.status === 'cancelled')) return 'cancelled';
  return client.subscriptionStatus;
}

function listSubscriptionStatusLabel(
  client: CreditBlockClient,
  resolvedStatus: CreditBlockClient['subscriptionStatus']
): string {
  if (resolvedStatus !== 'suspended') {
    return resolvedStatus.charAt(0).toUpperCase() + resolvedStatus.slice(1);
  }
  const cb = creditBlockSubscriptions(client.assignedProducts);
  const suspendedStage = cb
    .filter((p) => p.status === 'suspended')
    .map((p) => String(p.subscriptionStage ?? '').trim())
    .find(Boolean);
  return suspendedStage || 'Suspended';
}

const DEFAULT_CLIENTS_PAGE_SIZE = 10;
const RECENTLY_VIEWED_KEY = 'cb_recently_viewed_clients';
const MAX_RECENTLY_VIEWED = 25;

function readRecentlyViewedClientIds(): string[] {
  try {
    const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

interface CreditBlockAppProps {
  onBackToHome?: () => void;
  userRole?: string;
  initialTab?: string;
  /** URL-driven client detail (from /credit-block/client/:clientId) */
  urlClientId?: string;
  /** URL-driven case detail (from /credit-block/case/:caseId) */
  urlCaseId?: string;
  /** URL-driven task detail (from /credit-block/task/:taskId) */
  urlTaskId?: string;
}

const stripSuffix = (s: string) => (s ?? '').replace(/__stg3_c$|__stg_c$|__dev_c$|__c$/i, '');
const getErrorStatus = (err: unknown) =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status;

/** Stable empty list — avoids new array identity each render (exhaustive-deps on useMemos that depend on it). */
const EMPTY_CREDIT_BLOCK_CLIENTS: CreditBlockClient[] = [];
const CREDIT_BLOCK_TAB_IDS = new Set([
  'overview',
  'clients',
  'credit-blocks',
  'cases',
]);

/**
 * Loader: fetches a Case record by Gobodo ID and renders CaseDetailView.
 * Used when navigating from dashboard (which only has an ID, not the full record).
 */
const CaseDetailByIdLoader: React.FC<{ caseId: string; onBack: () => void; onOpenClientDetails?: (clientLookup: string) => void }> = ({
  caseId,
  onBack,
  onOpenClientDetails,
}) => {
  const CASE_OBJECT = `Case${getCreditBlockSuffix()}`;
  const { data: caseRecord, isLoading, isError } = useQuery({
    queryKey: ['credit-block', 'case-by-id', caseId],
    queryFn: async () => {
      const objApi = new GobodoApi.GraphObjectsApi(getGobodoClient());
      const numericId = /^\d+$/.test(caseId) ? Number(caseId) : null;
      const gobodoObj = numericId != null
        ? await objApi.getObjectById(numericId)
        : await objApi.getObjectByKey(caseId);
      const rec = convertFromGobodoRecord(gobodoObj, CASE_OBJECT) as Record<string, unknown>;
      const d = (rec.data as Record<string, unknown>) ?? rec;
      const numericObjectId = String(rec.id ?? d.id ?? caseId).trim();
      const result = {
        id: String((rec as { key?: string }).key ?? rec.id ?? caseId),
        numericObjectId,
        caseNumber: String(d.CaseNumber__c ?? d.caseNumber ?? '').trim() || numericObjectId,
        subject: String(d.Subject__c ?? d.subject ?? ''),
        status: String(d.Status__c ?? d.status ?? ''),
        priority: String(d.Priority__c ?? d.priority ?? ''),
        type: String(d.Type__c ?? d.type ?? ''),
        owner: String(d.owner_name ?? d.AccountOwner__c ?? d.OwnerId__c ?? ''),
        accountName: String(d.AccountName__c ?? d.accountName ?? ''),
        createdDate: String(d.CreatedDate__c ?? d.createdDate ?? ''),
        lastModifiedDate: String(d.LastModifiedDate__c ?? d.lastModifiedDate ?? ''),
        createdBy: String(d.created_by_name__stg3_c ?? d.created_by_name ?? d.CreatedById__c ?? ''),
        lastModifiedBy: String(d.last_modified_by_name__stg3_c ?? d.last_modified_by_name ?? d.LastModifiedById__c ?? ''),
        description: String(d.Description__c ?? d.description ?? ''),
        origin: String(d.Origin__c ?? d.origin ?? ''),
        subType: String(d.Sub_Type__c ?? d.subType ?? ''),
        ...d,
      } as any;

      // Resolve SF user IDs to display names
      const sfIds: string[] = [];
      if (looksLikeGraphUserSfId(result.createdBy)) sfIds.push(result.createdBy);
      if (looksLikeGraphUserSfId(result.lastModifiedBy)) sfIds.push(result.lastModifiedBy);
      if (sfIds.length > 0) {
        const nameMap = await resolveGraphUserNamesBySfIds(sfIds);
        const createdName = nameMap.get(result.createdBy);
        if (createdName) result.createdBy = createdName;
        const modifiedName = nameMap.get(result.lastModifiedBy);
        if (modifiedName) result.lastModifiedBy = modifiedName;
      }
      return result;
    },
    enabled: !!caseId,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading case…</span>
      </div>
    );
  }
  if (isError || !caseRecord) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">Failed to load case.</p>
        <button type="button" onClick={onBack} className="text-blue-600 hover:underline text-sm">Back</button>
      </div>
    );
  }
  return (
    <CaseDetailView
      caseRecord={caseRecord}
      clientName={caseRecord.accountName || ''}
      onBack={onBack}
      onOpenClientDetails={onOpenClientDetails}
    />
  );
};

/**
 * Loader: fetches a Task record by Gobodo ID and renders it as a CaseDetailView-style page.
 * Tasks share similar fields (subject, status, priority, description, owner).
 */
const TaskDetailByIdLoader: React.FC<{ taskId: string; onBack: () => void }> = ({ taskId, onBack }) => {
  const TASK_OBJECT = `Task${getCreditBlockSuffix()}`;
  const { data: taskRecord, isLoading, isError } = useQuery({
    queryKey: ['credit-block', 'task-by-id', taskId],
    queryFn: async () => {
      const objApi = new GobodoApi.GraphObjectsApi(getGobodoClient());
      const numericId = /^\d+$/.test(taskId) ? Number(taskId) : null;
      const gobodoObj = numericId != null
        ? await objApi.getObjectById(numericId)
        : await objApi.getObjectByKey(taskId);
      const rec = convertFromGobodoRecord(gobodoObj, TASK_OBJECT) as Record<string, unknown>;
      const d = (rec.data as Record<string, unknown>) ?? rec;
      const result = {
        id: String((rec as { key?: string }).key ?? rec.id ?? taskId),
        subject: String(d.Subject__c ?? d.subject ?? d.Subject ?? ''),
        status: String(d.Status__c ?? d.status ?? d.Status ?? ''),
        priority: String(d.Priority__c ?? d.priority ?? d.Priority ?? ''),
        type: String(d.Type__c ?? d.type ?? 'Task'),
        owner: String(d.owner_name ?? d.owner_name__stg3_c ?? d.OwnerId__c ?? ''),
        accountName: String(d.AccountName__c ?? d.accountName ?? ''),
        createdDate: String(d.CreatedDate__c ?? d.createdDate ?? d.created_date ?? ''),
        lastModifiedDate: String(d.LastModifiedDate__c ?? d.lastModifiedDate ?? ''),
        createdBy: String(d.created_by_name__stg3_c ?? d.created_by_name ?? d.CreatedById__c ?? ''),
        lastModifiedBy: String(d.last_modified_by_name__stg3_c ?? d.last_modified_by_name ?? d.LastModifiedById__c ?? ''),
        description: String(d.Description__c ?? d.description ?? d.Description ?? ''),
        activityDate: String(d.ActivityDate__c ?? d.activity_date ?? ''),
        ...d,
      } as any;

      // Resolve SF user IDs to display names
      const sfIds: string[] = [];
      if (looksLikeGraphUserSfId(result.createdBy)) sfIds.push(result.createdBy);
      if (looksLikeGraphUserSfId(result.lastModifiedBy)) sfIds.push(result.lastModifiedBy);
      if (sfIds.length > 0) {
        const nameMap = await resolveGraphUserNamesBySfIds(sfIds);
        const createdName = nameMap.get(result.createdBy);
        if (createdName) result.createdBy = createdName;
        const modifiedName = nameMap.get(result.lastModifiedBy);
        if (modifiedName) result.lastModifiedBy = modifiedName;
      }
      return result;
    },
    enabled: !!taskId,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-500 dark:text-gray-400">Loading task…</span>
      </div>
    );
  }
  if (isError || !taskRecord) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-600 dark:text-red-400 mb-4">Failed to load task.</p>
        <button type="button" onClick={onBack} className="text-blue-600 hover:underline text-sm">Back</button>
      </div>
    );
  }
  // Reuse CaseDetailView for tasks — it handles the same field structure
  return <CaseDetailView caseRecord={taskRecord} clientName={taskRecord.accountName || ''} onBack={onBack} />;
};

const normalizeCreditBlockTab = (tab?: string): string => {
  const t = String(tab ?? '').trim().toLowerCase();
  return CREDIT_BLOCK_TAB_IDS.has(t) ? t : 'overview';
};

const CreditBlockApp: React.FC<CreditBlockAppProps> = ({ onBackToHome, userRole = 'employee', initialTab, urlClientId, urlCaseId, urlTaskId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { setCurrentApplication } = useApplicationContext();
  const { userName } = useCurrentUser();
  // CB-004: Add client is admin-only. Matches the role check at
  // utils/caslConverter.ts:219 (`role.isSystem || name === 'System Administrator'`).
  // When mvpConfig.bypassRoleChecks=true (current MVP default), all users
  // get the system-admin ability and the gate is a no-op — intentional.
  const { roles } = useAbilityContext();
  const isSystemAdmin = (roles ?? []).some(
    (r) => (r as { isSystem?: boolean }).isSystem || r.name === 'System Administrator',
  );
  const [selectedTab, setSelectedTab] = useState(() => normalizeCreditBlockTab(initialTab));
  const [currentView, setCurrentView] = useState<'operations-dashboard' | 'client-detail' | 'case-detail' | 'task-detail'>(
    'operations-dashboard'
  );
  // The Team dashboard toggle visibility must use resolved roles (Gobodo +
  // actual Keycloak realm roles), NOT the userRole prop from App.tsx — that
  // prop falls back to getMVPDefaultRole() = 'system_admin' in dev bypass
  // mode regardless of who's logged in, so checking userRole shows the
  // toggle to every dev user including Processors. The resolved roles below
  // are the same source the RBAC Debug panel uses.
  const TEAM_DASHBOARD_GOBODO_ROLES = ['Operations Manager', 'System Administrator', 'Credit Block Team Lead'];
  const TEAM_DASHBOARD_KEYCLOAK_ROLES = ['manager', 'system_admin', 'cb_supervisor'];
  const hasTeamDashboardGobodoRole = (roles ?? []).some((r) => TEAM_DASHBOARD_GOBODO_ROLES.includes(r.name));
  const hasTeamDashboardKeycloakRole = keycloakService.hasAnyRole(TEAM_DASHBOARD_KEYCLOAK_ROLES);
  const canSeeTeamDashboard = hasTeamDashboardGobodoRole || hasTeamDashboardKeycloakRole;
  const [opsHomeMode, setOpsHomeMode] = useState(() => readCreditBlockOpsHomeMode(userRole));

  // Keep opsHomeMode in sync with resolved role
  const prevUserRole = useRef(userRole);
  useEffect(() => {
    if (prevUserRole.current === userRole) return;
    prevUserRole.current = userRole;
    if (canSeeTeamDashboard) {
      setOpsHomeMode(readCreditBlockOpsHomeMode(userRole));
    } else {
      setOpsHomeMode('processor');
    }
  }, [userRole, canSeeTeamDashboard]);
  const [selectedClient, setSelectedClient] = useState<CreditBlockClient | null>(null);
  const [selectedCaseId, setSelectedCaseId] = useState<string | null>(null);
  const [selectedCaseRecord, setSelectedCaseRecord] = useState<ListViewRecord | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  /** Debounced value passed to Gobodo traversal search (avoids a request per keystroke). */
  const [debouncedClientSearch, setDebouncedClientSearch] = useState('');
  const [casesSearchQuery, setCasesSearchQuery] = useState('');
  const [debouncedCasesSearch, setDebouncedCasesSearch] = useState('');
  const [unlockRequestsSearchQuery, setUnlockRequestsSearchQuery] = useState('');
  const [unlockRequestsDateSort, setUnlockRequestsDateSort] = useState<UnlockRequestSortOrder>('newest');
  const [clientListView, setClientListView] = useState<'all' | 'recently_viewed'>('recently_viewed');
  const [recentlyViewedIds, setRecentlyViewedIds] = useState<string[]>(() => readRecentlyViewedClientIds());
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientsPage, setClientsPage] = useState(() => {
    const raw = new URLSearchParams(location.search).get('page');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
  });
  const [clientsPageSize, setClientsPageSize] = useState(DEFAULT_CLIENTS_PAGE_SIZE);
  const [unlockRequestsPage, setUnlockRequestsPage] = useState(1);
  const [unlockRequestsPageSize, setUnlockRequestsPageSize] = useState(10);
  const [casesPage, setCasesPage] = useState(1);
  const [casesPageSize, setCasesPageSize] = useState(DEFAULT_CASES_PAGE_SIZE);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsPageSize, setReportsPageSize] = useState(10);
  const [creditBlocksPage, setCreditBlocksPage] = useState(() => {
    const raw = new URLSearchParams(location.search).get('page');
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
  });
  const [creditBlocksPageSize, setCreditBlocksPageSize] = useState(25);
  const [creditBlocksStatusFilter, setCreditBlocksStatusFilter] = useState<'active' | 'all'>('active');
  const [creditBlocksSearchQuery, setCreditBlocksSearchQuery] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState<
    | 'all'
    | 'unlock_request'
    | 'lock_request'
    | 'permanent_block'
    | 'permanent_unblock'
    | 'extension'
    | 'special_case'
  >('all');
  const [selectedRequest, setSelectedRequest] = useState<UnlockRequestWithClient | null>(null);
  const [opsErrorDismissed, setOpsErrorDismissed] = useState(false);
  const [newCaseModalOpen, setNewCaseModalOpen] = useState(false);
  const [quickAddClientOpen, setQuickAddClientOpen] = useState(false);
  const [creditPullOpen, setCreditPullOpen] = useState(false);
  const [creditPullClientId, setCreditPullClientId] = useState('');
  const [creditPullClientName, setCreditPullClientName] = useState('');

  // Set application context for page layout detection
  useEffect(() => {
    console.log('🎯 CreditBlockApp mounted - setting application context to CreditBlock');
    setCurrentApplication('CreditBlock');

    // Reset to Global when unmounting
    return () => {
      console.log('🎯 CreditBlockApp unmounting - resetting application context to Global');
      setCurrentApplication('Global');
    };
  }, [setCurrentApplication]);

  useEffect(() => {
    try {
      localStorage.setItem(CREDIT_BLOCK_OPS_HOME_MODE_STORAGE_KEY, opsHomeMode);
    } catch {
      /* ignore */
    }
  }, [opsHomeMode]);

  useEffect(() => {
    const syncRecentlyViewed = () => setRecentlyViewedIds(readRecentlyViewedClientIds());
    window.addEventListener('storage', syncRecentlyViewed);
    return () => window.removeEventListener('storage', syncRecentlyViewed);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedClientSearch(searchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedCasesSearch(casesSearchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [casesSearchQuery]);

  useEffect(() => {
    const next = normalizeCreditBlockTab(initialTab);
    setSelectedTab((prev) => (prev === next ? prev : next));
  }, [initialTab]);

  // Handle URL-driven navigation (deep links, browser back/forward)
  useEffect(() => {
    if (urlClientId) {
      const selectedKey = stripSuffix(selectedClient?.id ?? '');
      const targetKey = stripSuffix(urlClientId);
      if (currentView !== 'client-detail') setCurrentView('client-detail');
      if (!selectedKey || selectedKey !== targetKey) {
        // URL says /credit-block/client/:id[/:section] — open client detail (preserve section when deep-linking)
        void openCreditBlockClient(urlClientId, { clearCase: true, pathname: location.pathname });
      }
    } else if (urlCaseId && currentView !== 'case-detail') {
      setSelectedCaseId(urlCaseId);
      setCurrentView('case-detail');
    } else if (urlTaskId && currentView !== 'task-detail') {
      setSelectedTaskId(urlTaskId);
      setCurrentView('task-detail');
    } else if (!urlClientId && !urlCaseId && !urlTaskId && currentView !== 'operations-dashboard') {
      // URL is just /credit-block/:tab — go back to dashboard
      setCurrentView('operations-dashboard');
      setSelectedClient(null);
      setSelectedCaseId(null);
      setSelectedTaskId(null);
    }
  }, [urlClientId, urlCaseId, urlTaskId, currentView, selectedClient, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (tabId: string) => {
    const next = normalizeCreditBlockTab(tabId);
    setSelectedTab(next);
    if (next === 'clients') {
      const targetPage = Math.max(1, clientsPage);
      navigate(`/credit-block/${next}${targetPage > 1 ? `?page=${targetPage}` : ''}`);
      return;
    }
    navigate(`/credit-block/${next}`);
  };

  /** Drop `?page=` so URL sync does not force a deep page while filtering "Recently viewed" to an empty slice. */
  const clearClientsPageQueryParam = () => {
    const params = new URLSearchParams(location.search);
    if (!params.has('page')) return;
    params.delete('page');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  };

  /** Full list: page 1, clean URL, refetch so we do not rely on a stale paginated cache after the RV filter. */
  const handleSwitchToAllClientsList = () => {
    setClientListView('all');
    setClientsPage(1);
    clearClientsPageQueryParam();
    void queryClient.invalidateQueries({ queryKey: ['credit-block', 'clients', 'paginated'] });
  };

  /** Recently viewed reads from the current API page; start at page 1 so local IDs can match the first traversal page. */
  const handleSwitchToRecentlyViewedClientsList = () => {
    setClientListView('recently_viewed');
    setRecentlyViewedIds(readRecentlyViewedClientIds());
    setClientsPage(1);
    clearClientsPageQueryParam();
  };

  const addToRecentlyViewed = (clientId: string) => {
    const ids = readRecentlyViewedClientIds().filter((id) => id !== clientId);
    const nextIds = [clientId, ...ids].slice(0, MAX_RECENTLY_VIEWED);
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(nextIds));
    setRecentlyViewedIds(nextIds);
  };

  const handleClientSelect = (client: CreditBlockClient) => {
    void openCreditBlockClient(client.id);
  };

  const handleBackToDashboard = () => {
    const nextTab = 'clients';
    setSelectedTab(nextTab);
    setCurrentView('operations-dashboard');
    setSelectedClient(null);
    setSelectedCaseId(null);
    setSelectedCaseRecord(null);
    setSelectedTaskId(null);
    navigate(`/credit-block/${nextTab}`, { replace: false });
  };

  const handleCaseSelect = (caseRowOrId: ListViewRecord | string) => {
    const caseId =
      typeof caseRowOrId === 'string'
        ? caseRowOrId
        : String(
            (caseRowOrId as { key?: unknown }).key ??
              (caseRowOrId as { sf_id?: unknown }).sf_id ??
              caseRowOrId.id ??
              caseRowOrId.caseNumber ??
              ''
          ).trim();
    setSelectedCaseId(caseId);
    setSelectedCaseRecord(typeof caseRowOrId === 'string' ? null : caseRowOrId);
    setCurrentView('case-detail');
    navigate(`/credit-block/case/${caseId}`, { replace: false });
  };

  const handleBackFromCaseDetail = () => {
    setSelectedCaseId(null);
    setSelectedCaseRecord(null);
    setCurrentView('operations-dashboard');
    changeTab('cases');
  };

  // Full Credit Block client list is not loaded app-wide (heavy). Cases tab uses Record Type→Case traversal; use paginated clients on the Clients tab.
  const allClients = EMPTY_CREDIT_BLOCK_CLIENTS;
  const overviewLoading = false;
  const overviewError = false;
  const overviewErrorDetail: unknown = undefined;
  const {
    data: reportsPaginated,
    isLoading: reportsLoading,
  } = useCreditReportsPaginated(reportsPage, reportsPageSize, selectedTab === 'reports');
  const visibleCreditReports = selectedTab === 'reports' ? (reportsPaginated?.reports ?? []) : [];
  const reportsTotalCount = reportsPaginated?.totalCount ?? 0;
  const reportHydrationQueries = useCreditReportHydrationQueries(
    visibleCreditReports,
    selectedTab === 'reports' && visibleCreditReports.length > 0
  );
  const lastReportsTotalCountRef = useRef(0);
  if (reportsTotalCount > 0) lastReportsTotalCountRef.current = reportsTotalCount;
  const reportsTotalItemsStable =
    reportsLoading && lastReportsTotalCountRef.current > 0
      ? lastReportsTotalCountRef.current
      : reportsTotalCount;
  const reportsTotalPagesStable = Math.max(1, Math.ceil(reportsTotalItemsStable / reportsPageSize));
  const allCreditReports = useMemo(
    () =>
      visibleCreditReports.map((report, index) => {
        const hydration = reportHydrationQueries[index]?.data;
        if (!hydration) return report;
        return {
          ...report,
          clientId: hydration.clientId || report.clientId,
          clientName: hydration.clientName || report.clientName,
          systemInfo: {
            ...report.systemInfo,
            createdBy: hydration.createdBy || report.systemInfo?.createdBy || '',
          },
        };
      }),
    [visibleCreditReports, reportHydrationQueries]
  );

  const { data: paginatedResult, isLoading: paginatedLoading } = useCreditBlockClientsPaginated(
    clientsPage,
    clientsPageSize,
    statusFilter,
    selectedTab === 'clients' && clientListView === 'all',
    debouncedClientSearch || undefined
  );
  const { data: clientsExactCount } = useCreditBlockClientsExactCount(
    statusFilter,
    selectedTab === 'clients' && clientListView === 'all',
    debouncedClientSearch || undefined
  );
  const {
    data: recentlyViewedClients = [],
    isLoading: recentlyViewedLoading,
  } = useRecentlyViewedCreditBlockClients(
    recentlyViewedIds,
    selectedTab === 'clients' && clientListView === 'recently_viewed'
  );
  const {
    data: creditBlocksTabResult,
    isLoading: creditBlocksTabLoading,
  } = useCreditBlockSubscriptionsPaginated(
    creditBlocksPage,
    creditBlocksPageSize,
    selectedTab === 'credit-blocks',
    creditBlocksStatusFilter,
  );
  const creditBlocksTabRows = creditBlocksTabResult?.subscriptions ?? [];
  const creditBlocksTotalCount = creditBlocksTabResult?.totalCount ?? 0;
  const lastCreditBlocksTotalCountRef = useRef(0);
  if (creditBlocksTotalCount > 0) lastCreditBlocksTotalCountRef.current = creditBlocksTotalCount;
  const creditBlocksTotalItemsStable = creditBlocksTabLoading && lastCreditBlocksTotalCountRef.current > 0
    ? lastCreditBlocksTotalCountRef.current
    : creditBlocksTotalCount > 0
      ? creditBlocksTotalCount
      : creditBlocksTabRows.length;
  const creditBlocksTotalPagesStable = Math.max(1, Math.ceil(creditBlocksTotalItemsStable / creditBlocksPageSize));
  const handleCreditBlocksPageSizeChange = useCallback((size: number) => {
    setCreditBlocksPageSize(size);
    setCreditBlocksPage(1);
  }, []);
  /** First page for New case / Credit pull pickers only when a modal needs it (avoids heavy traversal on every load). */
  const { data: newCaseModalClientsPage, isLoading: newCaseModalClientsLoading } = useCreditBlockClientsPaginated(
    1,
    CREDIT_BLOCK_BULK_LIST_SIZE,
    'all',
    newCaseModalOpen || creditPullOpen
  );
  const newCaseModalExtraClients = useMemo(
    () =>
      mergeClientsByPickId(newCaseModalClientsPage?.clients ?? [], paginatedResult?.clients ?? []),
    [newCaseModalClientsPage, paginatedResult]
  );

  /** Open Credit Block client detail in-app (never Product Hub `/clients/:id`). */
  const openCreditBlockClient = async (
    lookup: string,
    options?: { clearCase?: boolean; clearTask?: boolean; pathname?: string }
  ) => {
    const raw = String(lookup ?? '').trim();
    if (!raw) return;
    const cid = toClientGobodoObjectKey(raw) || raw;
    const fetchedClient =
      (await fetchCreditBlockClientByLookup(cid)) ??
      (raw === cid ? undefined : await fetchCreditBlockClientByLookup(raw)) ??
      undefined;
    const pool = [...(paginatedResult?.clients ?? []), ...newCaseModalExtraClients, ...allClients];
    const seen = new Set<string>();
    let client: CreditBlockClient | undefined;
    if (fetchedClient) {
      client = fetchedClient;
    } else {
      for (const c of pool) {
        const id = c.id ?? '';
        if (!id || seen.has(id)) continue;
        seen.add(id);
        if (id === cid || id === raw || stripSuffix(id) === stripSuffix(cid)) {
          client = c;
          break;
        }
      }
    }
    if (client) {
      if (options?.clearCase) setSelectedCaseId(null);
      if (options?.clearCase) setSelectedCaseRecord(null);
      if (options?.clearTask) setSelectedTaskId(null);
      setSelectedClient(client);
      setCurrentView('client-detail');
      addToRecentlyViewed(client.id);
      const clientKey = stripSuffix(client.id);
      const path = String(options?.pathname ?? location.pathname ?? '');
      const sectionMatch = path.match(/\/credit-block\/client\/[^/]+\/([^/]+)\/?(?:\?|$)/);
      const fromUrl = sectionMatch?.[1]?.trim();
      const sectionSlug =
        fromUrl && isKnownCreditBlockClientSection(fromUrl) ? fromUrl : CREDIT_BLOCK_CLIENT_DEFAULT_SECTION;
      navigate(`/credit-block/client/${encodeURIComponent(clientKey)}/${sectionSlug}`, { replace: false });
      return;
    }
    setCurrentView('operations-dashboard');
    changeTab('clients');
  };

  const handleNavigateToClientFromCase = (clientId: string) => {
    void openCreditBlockClient(clientId, { clearCase: true });
  };

  const {
    data: casesPaginated,
    isPending: casesPending,
    isError: casesError,
    error: casesQueryError,
  } = useCreditBlockCasesPaginated(
    [],
    casesPage,
    casesPageSize,
    selectedTab === 'cases' && currentView === 'operations-dashboard',
    { creditBlockRootCases: true },
    debouncedCasesSearch
  );
  const casesTotalCount = casesPaginated?.totalCount ?? 0;
  const creditBlockCases = useMemo(() => {
    const raw = casesPaginated?.cases ?? [];
    const q = debouncedCasesSearch.trim().toLowerCase();
    if (!q) return raw;
    return raw.filter((c) => {
      const subject = String(c.subject ?? '').toLowerCase();
      const status = String(c.status ?? '').toLowerCase();
      const owner = String(c.owner ?? '').toLowerCase();
      const priority = String(c.priority ?? '').toLowerCase();
      const accountName = String(c.accountName ?? '').toLowerCase();
      const caseNum = String(c.caseNumber ?? c.id ?? '').toLowerCase();
      return subject.includes(q) || status.includes(q) || owner.includes(q) || priority.includes(q) || accountName.includes(q) || caseNum.includes(q);
    });
  }, [casesPaginated, debouncedCasesSearch]);
  const casesSearchPending =
    selectedTab === 'cases' &&
    currentView === 'operations-dashboard' &&
    casesSearchQuery.trim() !== debouncedCasesSearch &&
    (casesSearchQuery.trim().length > 0 || debouncedCasesSearch.length > 0);
  const casesLoading = casesPending || casesSearchPending;
  /** Case.accountId per row (same order as table) — used for getObjectByKey / getObjectById client fetch. */
  const casePageClientLookups = useMemo(() => {
    if (selectedTab !== 'cases' || currentView !== 'operations-dashboard') return [];
    const rows = casesPaginated?.cases;
    if (!rows?.length) return [];
    return rows.map((c) => {
      const accountName = String(c.accountName ?? '').trim();
      if (accountName) return '';
      return String(c.accountId ?? '').trim();
    });
  }, [selectedTab, currentView, casesPaginated]);
  const caseLinkedClientQueries = useCreditBlockCaseLinkedClientQueries(
    casePageClientLookups,
    selectedTab === 'cases' &&
      currentView === 'operations-dashboard' &&
      casePageClientLookups.length > 0
  );
  const accountIdToName = useMemo(() => {
    const map = new Map<string, string>();
    allClients.forEach((c: { id?: string; accountName?: string }) => {
      if (c.id) map.set(c.id, c.accountName ?? '');
      const norm = stripSuffix(c.id ?? '');
      if (norm) map.set(norm, c.accountName ?? '');
    });
    return map;
  }, [allClients]);

  const paginatedTotalCount = clientsExactCount ?? paginatedResult?.totalCount ?? 0;
  const filteredRecentlyViewedClients = useMemo(() => {
    let clients = recentlyViewedClients;
    const effectiveStatusFilter = statusFilter === 'all' ? '' : statusFilter.toLowerCase();
    if (effectiveStatusFilter) {
      clients = clients.filter((c) => String(c.subscriptionStatus ?? '').toLowerCase() === effectiveStatusFilter);
    }
    const q = debouncedClientSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const name = String(c.accountName ?? '').toLowerCase();
      const email = String(c.contact?.email ?? '').toLowerCase();
      const owner = String(c.accountOwner ?? '').toLowerCase();
      const id = String(c.id ?? '').toLowerCase();
      return name.includes(q) || email.includes(q) || owner.includes(q) || id.includes(q);
    });
  }, [recentlyViewedClients, statusFilter, debouncedClientSearch]);
  const allPageClients =
    selectedTab !== 'clients'
      ? EMPTY_CREDIT_BLOCK_CLIENTS
      : clientListView === 'recently_viewed'
        ? filteredRecentlyViewedClients
        : (paginatedResult?.clients ?? []);
  const pageClients = allPageClients;
  const pageClientIds = selectedTab === 'clients' ? pageClients.map((c) => c.id).filter(Boolean) as string[] : [];
  const joinRowQueries = useCreditBlockClientsJoinQueries(
    pageClientIds,
    selectedTab === 'clients' && pageClientIds.length > 0
  );
  const totalCount =
    selectedTab !== 'clients'
      ? 0
      : clientListView === 'recently_viewed'
        ? filteredRecentlyViewedClients.length
        : paginatedTotalCount;
  const clientSearchPending =
    selectedTab === 'clients' &&
    clientListView === 'all' &&
    searchQuery.trim() !== debouncedClientSearch &&
    (searchQuery.trim().length > 0 || debouncedClientSearch.length > 0);
  const clientsLoading =
    selectedTab === 'clients' &&
    (clientListView === 'recently_viewed' ? recentlyViewedLoading : (paginatedLoading || clientSearchPending));
  const {
    data: unlockRequestsAll,
    isPending: unlockRequestsPending,
    isError: unlockRequestsError,
    error: unlockRequestsQueryError,
  } = useCreditBlockUnlockRequests(selectedTab === 'unlock-requests');
  const unlockRequestsFiltered = useMemo(() => {
    const raw = unlockRequestsAll ?? [];
    const byType =
      requestTypeFilter === 'all'
        ? raw
        : raw.filter((r) => String(r.type ?? '').trim() === requestTypeFilter);
    const q = unlockRequestsSearchQuery.trim().toLowerCase();
    const filtered = !q
      ? byType
      : byType.filter((r) => {
      const type = String(r.type ?? '').toLowerCase();
      const bureau = String(r.bureau ?? '').toLowerCase();
      const status = String(r.status ?? '').toLowerCase();
      const clientName = String(r.clientName ?? '').toLowerCase();
      const clientId = String(r.clientId ?? '').toLowerCase();
      const desc = String(r.description ?? '').toLowerCase();
      const reason = String(r.reason ?? '').toLowerCase();
      return (
        type.includes(q) ||
        bureau.includes(q) ||
        status.includes(q) ||
        clientName.includes(q) ||
        clientId.includes(q) ||
        desc.includes(q) ||
        reason.includes(q)
      );
    });
    return filtered.sort((a, b) => {
      const ta = new Date(a.requestDate ?? '').getTime();
      const tb = new Date(b.requestDate ?? '').getTime();
      const safeA = Number.isNaN(ta) ? 0 : ta;
      const safeB = Number.isNaN(tb) ? 0 : tb;
      return unlockRequestsDateSort === 'newest' ? safeB - safeA : safeA - safeB;
    });
  }, [unlockRequestsAll, requestTypeFilter, unlockRequestsSearchQuery, unlockRequestsDateSort]);
  const unlockRequestsTotalCount = unlockRequestsFiltered.length;
  const unlockRequestsTableRows = useMemo(() => {
    const start = Math.max(0, (unlockRequestsPage - 1) * unlockRequestsPageSize);
    const end = start + unlockRequestsPageSize;
    return unlockRequestsFiltered.slice(start, end);
  }, [unlockRequestsFiltered, unlockRequestsPage, unlockRequestsPageSize]);
  /** Same order as rendered rows — per-row GraphObjectsApi client fetch (suffix-aware), TanStack dedupes by normalized lookup. */
  const unlockRequestClientLookups = useMemo(() => {
    if (selectedTab !== 'unlock-requests') return [];
    const rows = unlockRequestsTableRows;
    if (!rows?.length) return [];
    return rows.map((r) => {
      const name = String(r.clientName ?? '').trim();
      if (name) return '';
      const lookup = String(r.clientId ?? '').trim();
      const low = lookup.toLowerCase();
      if (!lookup || low === '-' || low === '—' || low === 'null' || low === 'undefined' || low === 'n/a') return '';
      return lookup;
    });
  }, [selectedTab, unlockRequestsTableRows]);
  const unlockRequestLinkedClientQueries = useCreditBlockCaseLinkedClientQueries(
    unlockRequestClientLookups,
    selectedTab === 'unlock-requests' && unlockRequestClientLookups.length > 0
  );
  const lastTotalCountRef = useRef(0);
  if (totalCount > 0) lastTotalCountRef.current = totalCount;
  const totalItemsForPagination = totalCount > 0 ? totalCount : pageClients.length;
  const totalPages = Math.max(1, Math.ceil(totalItemsForPagination / clientsPageSize));
  const totalPagesStable = clientsLoading && lastTotalCountRef.current > 0
    ? Math.max(1, Math.ceil(lastTotalCountRef.current / clientsPageSize))
    : totalPages;
  const totalItemsStable = clientsLoading && lastTotalCountRef.current > 0 ? lastTotalCountRef.current : totalItemsForPagination;
  const pageClamped = Math.min(Math.max(1, clientsPage), totalPagesStable);


  useEffect(() => {
    setClientsPage(1);
  }, [debouncedClientSearch, statusFilter]);

  useEffect(() => {
    setUnlockRequestsPage(1);
  }, [requestTypeFilter]);

  useEffect(() => {
    setUnlockRequestsPage(1);
  }, [unlockRequestsSearchQuery, unlockRequestsDateSort]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(unlockRequestsTotalCount / unlockRequestsPageSize));
    if (unlockRequestsPage > maxPage) setUnlockRequestsPage(maxPage);
  }, [unlockRequestsTotalCount, unlockRequestsPageSize, unlockRequestsPage]);

  useEffect(() => {
    setCasesPage(1);
  }, [debouncedCasesSearch]);

  useEffect(() => {
    if (selectedTab !== 'reports') return;
    setReportsPage(1);
  }, [selectedTab]);

  useEffect(() => {
    if (creditBlocksTabLoading) return;
    if (creditBlocksPage > creditBlocksTotalPagesStable) setCreditBlocksPage(creditBlocksTotalPagesStable);
  }, [creditBlocksTabLoading, creditBlocksPage, creditBlocksTotalPagesStable]);

  /** URL → state: when the URL ?page changes (back button, deep link) and the
   * credit-blocks tab is active, mirror it into local state. */
  useEffect(() => {
    if (currentView !== 'operations-dashboard' || selectedTab !== 'credit-blocks') return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('page');
    const parsed = Number(raw);
    const pageFromUrl = Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
    if (pageFromUrl !== creditBlocksPage) setCreditBlocksPage(pageFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, selectedTab, currentView]);

  /** State → URL: when local pagination state changes, push to the URL so
   * back/forward + deep-linking work. ?page=1 is the default and gets
   * stripped to keep the URL clean. */
  useEffect(() => {
    if (currentView !== 'operations-dashboard' || selectedTab !== 'credit-blocks') return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('page');
    const currentPageInUrl = Number(raw);
    const normalizedCurrentPageInUrl =
      Number.isFinite(currentPageInUrl) && currentPageInUrl >= 1 ? Math.floor(currentPageInUrl) : 1;
    const nextPage = Math.max(1, creditBlocksPage);
    if (normalizedCurrentPageInUrl === nextPage) return;
    if (nextPage <= 1) params.delete('page');
    else params.set('page', String(nextPage));
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: false });
  }, [creditBlocksPage, selectedTab, currentView, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (reportsLoading) return;
    if (reportsPage > reportsTotalPagesStable) setReportsPage(reportsTotalPagesStable);
  }, [reportsLoading, reportsPage, reportsTotalPagesStable]);

  useEffect(() => {
    if (!clientsLoading && totalPagesStable >= 1 && clientsPage > totalPagesStable) setClientsPage(totalPagesStable);
  }, [clientsLoading, totalPagesStable, clientsPage]);

  useEffect(() => {
    if (selectedTab !== 'clients') return;
    if (clientsLoading) return;
    if (clientsPage <= 1) return;
    if (totalCount <= 0) return;
    if (pageClients.length > 0) return;
    setClientsPage((prev) => Math.max(1, prev - 1));
  }, [selectedTab, clientsLoading, clientsPage, totalCount, pageClients.length]);

  useEffect(() => {
    if (currentView !== 'operations-dashboard' || selectedTab !== 'clients') return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('page');
    const parsed = Number(raw);
    const pageFromUrl = Number.isFinite(parsed) && parsed >= 1 ? Math.floor(parsed) : 1;
    if (pageFromUrl !== clientsPage) setClientsPage(pageFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, selectedTab, currentView]);

  useEffect(() => {
    if (currentView !== 'operations-dashboard' || selectedTab !== 'clients') return;
    const params = new URLSearchParams(location.search);
    const raw = params.get('page');
    const currentPageInUrl = Number(raw);
    const normalizedCurrentPageInUrl =
      Number.isFinite(currentPageInUrl) && currentPageInUrl >= 1 ? Math.floor(currentPageInUrl) : 1;
    const nextPage = Math.max(1, clientsPage);
    if (normalizedCurrentPageInUrl === nextPage) return;
    if (nextPage <= 1) params.delete('page');
    else params.set('page', String(nextPage));
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ''}`, { replace: false });
  }, [clientsPage, selectedTab, currentView, location.pathname, location.search, navigate]);

  const handleClientsPageSizeChange = (size: number) => {
    setClientsPageSize(size);
    setClientsPage(1);
  };

  const handleUnlockRequestsPageSizeChange = (size: number) => {
    setUnlockRequestsPageSize(size);
    setUnlockRequestsPage(1);
  };

  const handleCasesPageSizeChange = (size: number) => {
    setCasesPageSize(size);
    setCasesPage(1);
  };

  const handleReportsPageSizeChange = (size: number) => {
    setReportsPageSize(size);
    setReportsPage(1);
  };

  const openCreditPullForClient = (client: CreditBlockClient) => {
    const directId = String(client.id ?? '').trim();
    const fallbackId = String(client.arrayInfo?.clientKey ?? '').trim();
    const clientId = directId || fallbackId;
    setCreditPullClientId(clientId);
    setCreditPullClientName((client.accountName || '').trim() || clientId || 'Select client');
    setCreditPullOpen(true);
  };

  const wrap = (children: React.ReactNode) => <GobodoRequiredGuard>{children}</GobodoRequiredGuard>;

  const renderDetailView = () => {
    if (currentView === 'client-detail' && !selectedClient) {
      return (
        <>
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading subscription workspace…</span>
          </div>
          <InternalChatbot
            applicationContext="credit_block"
            position="bottom-right"
          />
        </>
      );
    }
    if (currentView === 'client-detail' && selectedClient) {
      return (
        <>
          <CreditBlockClientDetail
            client={selectedClient}
            onBack={handleBackToDashboard}
            onPullCreditReport={openCreditPullForClient}
            onOpenClientDetails={handleNavigateToClientFromCase}
          />
          <InternalChatbot
            applicationContext="credit_block"
            clientContext={selectedClient}
            position="bottom-right"
          />
        </>
      );
    }
    if (currentView === 'case-detail' && selectedCaseId) {
      return (
        <>
          <CaseDetailByIdLoader
            caseId={selectedCaseId}
            onBack={handleBackFromCaseDetail}
            onOpenClientDetails={handleNavigateToClientFromCase}
          />
          <InternalChatbot
            applicationContext="credit_block"
            position="bottom-right"
          />
        </>
      );
    }
    if (currentView === 'task-detail' && selectedTaskId) {
      return (
        <>
          <TaskDetailByIdLoader
            taskId={selectedTaskId}
            onBack={() => { setSelectedTaskId(null); setCurrentView('operations-dashboard'); }}
          />
          <InternalChatbot
            applicationContext="credit_block"
            position="bottom-right"
          />
        </>
      );
    }
    return null;
  };

  // Salesforce ID heuristic. Standard SF IDs are 15 or 18 chars, alphanumeric,
  // and start with a 3-char prefix that identifies the object type (005 = User,
  // 00G = Group/Queue, 001 = Account, 003 = Contact, etc.).
  const SF_ID_RE = /^[A-Za-z0-9]{15}([A-Za-z0-9]{3})?$/;
  const looksLikeSfId = (s: string) => SF_ID_RE.test(s);
  const looksLikeSfQueueId = (s: string) => /^00G[A-Za-z0-9]{12,15}$/.test(s);
  const looksLikeId = (s: string | undefined) =>
    !s ||
    /^\d+$/.test(s) ||
    (s.length > 20 && /^[a-zA-Z0-9_-]+$/.test(s)) ||
    looksLikeSfId(s);
  /** Resolved name only; raw Owner/User ids are not shown as fake labels. Queue
   *  IDs get a friendly "Queue" label so users know the case is sitting in a
   *  queue rather than assigned to a person. */
  const displayOwner = (owner: string | undefined) => {
    const v = owner?.trim();
    if (!v) return '—';
    if (looksLikeSfQueueId(v)) return 'Queue';
    if (looksLikeId(v)) return '—';
    return v;
  };
  const ownerCellTitle = (owner: string | undefined) => {
    if (!owner?.trim()) return undefined;
    if (!looksLikeId(owner)) return undefined;
    if (looksLikeSfQueueId(owner)) return `Owned by Salesforce Queue (id: ${owner}). The case is sitting in a queue rather than assigned to an individual user.`;
    return `Owner not expanded in API response (raw: ${owner}). In SF this is a User lookup; sync or Graph should expose name on Client or expand OwnerId.`;
  };
  const formatPhone = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits[0] === '1') return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    if (digits.length > 10) return `+${digits.slice(0, digits.length - 10)} (${digits.slice(-10, -7)}) ${digits.slice(-7, -4)}-${digits.slice(-4)}`;
    return raw;
  };

  const BUREAU_LABELS: Record<string, string> = { equifax: 'Equifax', experian: 'Experian', transunion: 'TransUnion', chexsystems: 'ChexSystems' };
  const displayBureaus = (client: CreditBlockClient) => {
    const bc = client.bureauCredentials ?? {};
    const bureauKeys = ['equifax', 'experian', 'transunion', 'chexsystems'] as const;
    let blocked = 0;
    let unblocked = 0;
    let permUnblocked = 0;
    let withData = 0;
    const tooltipLines: string[] = [];
    for (const b of bureauKeys) {
      const cred = bc[b] as { status?: string; creditBureauId?: string; enrollmentDate?: string } | undefined;
      if (cred?.creditBureauId || cred?.enrollmentDate) withData++;
      const st = (cred?.status ?? '').toLowerCase();
      const displayStatus = cred?.status ? cred.status.charAt(0).toUpperCase() + cred.status.slice(1) : 'No data';
      tooltipLines.push(`${BUREAU_LABELS[b]}: ${displayStatus}`);
      if (st === 'locked' || st === 'blocked') blocked++;
      else if (st === 'permanently unblocked' || st === 'permanent unblock') permUnblocked++;
      else if (st === 'unlocked' || st === 'unblocked') unblocked++;
    }
    const total = blocked + unblocked + permUnblocked;
    if (total === 0 && withData === 0) return <span className="text-gray-400">—</span>;

    const label = `${blocked}/${total || 4}`;
    const Icon = blocked === total && total > 0 ? Lock : Unlock;
    const colorCls = blocked === total && total > 0
      ? 'text-emerald-700 dark:text-emerald-400'
      : permUnblocked === total && total > 0
        ? 'text-gray-400 dark:text-gray-500'
        : 'text-red-600 dark:text-red-400';
    const displayLabel = permUnblocked === total && total > 0 ? `0/${total}` : label;

    return (
      <span className={`relative group inline-flex items-center gap-1.5 ${colorCls} font-semibold text-sm cursor-help`}>
        <Icon className="h-3.5 w-3.5" />
        {displayLabel}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-50 w-44 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg shadow-lg py-2 px-3 pointer-events-none">
          {tooltipLines.map((line, i) => {
            const [bureau, status] = line.split(': ');
            const isBlocked = (status ?? '').toLowerCase().includes('locked') && !(status ?? '').toLowerCase().includes('unlocked');
            return (
              <span key={i} className="flex justify-between py-0.5">
                <span className="text-gray-300">{bureau}</span>
                <span className={isBlocked ? 'text-emerald-400' : 'text-red-400'}>{status}</span>
              </span>
            );
          })}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-gray-900 dark:border-t-gray-700" />
        </span>
      </span>
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300';
      case 'suspended':
        return 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300';
      case 'cancelled':
        return 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300';
      case 'inactive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'clients', label: 'Clients', icon: Users },
    { id: 'credit-blocks', label: 'Credit Blocks', icon: FileSearch },
    { id: 'cases', label: 'Cases', icon: Briefcase },
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <CreditBlockOverviewAssigneeTraversalPrefetch />
      <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        {canSeeTeamDashboard ? (
        <div
          className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 bg-gray-100 dark:bg-gray-800"
          role="group"
          aria-label="Operations home layout"
        >
          <button
            type="button"
            onClick={() => setOpsHomeMode('processor')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              opsHomeMode === 'processor'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            My work
          </button>
          <button
            type="button"
            onClick={() => setOpsHomeMode('manager')}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              opsHomeMode === 'manager'
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
            }`}
          >
            Team dashboard
          </button>
        </div>
        ) : null}
      </div>
      {opsHomeMode === 'processor'
        ? (
          <CreditBlockMyWorkOverview
            onTaskSelect={(id) => { setSelectedTaskId(id); setCurrentView('task-detail'); }}
            onCaseSelect={handleCaseSelect}
          />
        )
        : (
          <div className="space-y-6">
            <CreditBlockTeamOverview onTaskSelect={(id) => { setSelectedTaskId(id); setCurrentView('task-detail'); }} />
            <CreditBlockUnassignedCaseQueue
              onCaseSelect={handleCaseSelect}
              onAssign={async (caseId, assigneeName, assigneeUserId) => {
                const suffix = getCreditBlockSuffix() || '__c';
                // Re-throw failures so the queue's handleAssignSelect shows
                // an error toast instead of a misleading success toast.
                await updateRecord(
                  `Case${suffix}`,
                  caseId,
                  {
                    Owner__c: assigneeName,
                    owner: assigneeName,
                    ...(assigneeUserId ? { [`OwnerRelField${suffix}`]: assigneeUserId } : {}),
                    Status__c: 'Assigned',
                    Status: 'Assigned',
                    status: 'Assigned',
                    [`Status${suffix}`]: 'Assigned',
                  },
                  { gobodoPropertyPatch: true }
                );
                // Optimistic cache update — mutate the cached Cases array
                // in place to reflect the new owner + status, so the
                // unassigned queue immediately drops the row and other
                // panels (Recent Cases, Team Performance) reflect the new
                // owner. Avoiding the refetch is intentional: Gobodo's 60s
                // server-side cache on this query would return stale
                // (pre-write) data anyway, making invalidateQueries an
                // expensive no-op that triggers ~60 cascading downstream
                // calls without changing what we render.
                //
                // We mutate BOTH cache keys because different components
                // request the same data under different suffix variants:
                //   - Unassigned Queue: useGobodoRecords(`Case${suffix}`)
                //                       → ['gobodo-records', 'Case__stg5_c']
                //   - Recent Cases:     useGobodoRecords('Case__c')
                //                       → ['gobodo-records', 'Case__c']
                // The recordStorageService normalizes the suffix internally
                // so both fetch the same Gobodo data, but React Query caches
                // them separately. Updating one doesn't propagate.
                const applyOptimisticUpdate = (old: unknown) => {
                  if (!Array.isArray(old)) return old;
                  return old.map((row) => {
                    const rowId = String(
                      (row as { id?: unknown }).id
                      ?? (row as { key?: unknown }).key
                      ?? '',
                    );
                    if (rowId !== String(caseId)) return row;
                    return {
                      ...row,
                      Owner__c: assigneeName,
                      owner: assigneeName,
                      Status__c: 'Assigned',
                      Status: 'Assigned',
                      status: 'Assigned',
                      [`Status${suffix}`]: 'Assigned',
                      ...(assigneeUserId ? { [`OwnerRelField${suffix}`]: assigneeUserId } : {}),
                    };
                  });
                };
                queryClient.setQueryData(gobodoRecordKeys.byType(`Case${suffix}`), applyOptimisticUpdate);
                queryClient.setQueryData(gobodoRecordKeys.byType('Case__c'), applyOptimisticUpdate);
              }}
            />
          </div>
        )
      }
      </>
    </div>
  );

  const renderClientsTab = () => {
    const displayedClientCount =
      clientListView === 'recently_viewed'
        ? pageClients.length
        : totalCount > 0
          ? totalCount
          : pageClients.length;

    const showPageSuffix =
      clientListView === 'all' && totalCount > 0 && pageClients.length > 0 && totalCount > pageClients.length;

    return (
    <div className="space-y-6">
      <CreditBlockListHeader
        title="Clients"
        count={{
          displayed: displayedClientCount,
          label: 'client',
          loading: clientsLoading,
          suffix: showPageSuffix ? `(this page: ${pageClients.length})` : undefined,
        }}
        search={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search by name, email, account owner, or client ID...',
        }}
        filters={
          <>
            <StandardSelect
              aria-label="List view"
              value={clientListView}
              onChange={(e) => {
                if (e.target.value === 'recently_viewed') {
                  handleSwitchToRecentlyViewedClientsList();
                } else {
                  handleSwitchToAllClientsList();
                }
              }}
              options={[
                { value: 'recently_viewed', label: 'Recently Viewed' },
                { value: 'all', label: 'All Clients' },
              ]}
            />
            <StandardSelect
              aria-label="Status filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
                { value: 'cancelled', label: 'Cancelled' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </>
        }
      />

      {/* Dev: show hint when no clients */}
      {!clientsLoading && pageClients.length === 0 && clientListView === 'recently_viewed' && (
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">No recently viewed clients.</p>
          <button
            type="button"
            onClick={handleSwitchToAllClientsList}
            className="mt-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            View all clients
          </button>
        </div>
      )}
      {!clientsLoading && pageClients.length === 0 && clientListView === 'all' && process.env.NODE_ENV === 'development' && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
          No clients. Open DevTools → Console for <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">[CreditBlock] raw counts</code> (creditBlocks, bureaus, clients).
          If clients=0, the Client fetch failed (504) or returned empty. If clients&gt;0 but list empty, check for filter/join mismatch.
        </div>
      )}

      {/* Client List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        {clientsLoading ? (
          <div className="p-6 animate-pulse">
            <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded mb-4" />
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700 rounded" />
              ))}
            </div>
          </div>
        ) : (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[180px]">Client</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Account Owner</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[80px]">Bureaus</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[150px]">Billing State/Province</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider min-w-[140px]">Phone</th>
              <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
            {pageClients.map((client) => {
              const rowIdx = client.id ? pageClientIds.indexOf(client.id) : -1;
              const rowQ = rowIdx >= 0 ? joinRowQueries[rowIdx] : undefined;
              const cbs = rowQ?.data?.creditBlocks ?? [];
              const bureaus = rowQ?.data?.bureaus ?? [];
              const rowEnrichmentReady = rowQ?.data !== undefined;
              const displayClient = rowEnrichmentReady ? mergeJoinIntoClient(client, cbs, bureaus) : client;
              const joinRowPending = !!rowQ?.isPending;
              const rowSubStatus = listSubscriptionStatus(displayClient);
              const rowSubStatusLabel = listSubscriptionStatusLabel(displayClient, rowSubStatus);
              const billingState = String(displayClient.addresses?.mailing?.state ?? '').trim();
              const phone = String(displayClient.contact?.phone ?? displayClient.contact?.mobile ?? '').trim();
              return (
              <tr key={client.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" onClick={() => handleClientSelect(displayClient)}>
                <td className="px-3 sm:px-6 py-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate block">{displayClient.accountName}</span>
                </td>
                <td
                  className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100"
                  title={ownerCellTitle(displayClient.accountOwner)}
                >
                  {displayOwner(displayClient.accountOwner)}
                </td>
                <td className="px-3 sm:px-6 py-4 text-sm">
                  {joinRowPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden />
                  ) : (
                    displayBureaus(displayClient)
                  )}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {billingState || '—'}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {phone ? formatPhone(phone) : '—'}
                </td>
                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                  {joinRowPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" aria-hidden />
                  ) : (
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(rowSubStatus)}`}>
                      {rowSubStatusLabel}
                    </span>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
        )}
        {!clientsLoading && clientListView === 'all' && (totalCount > 0 || pageClients.length > 0) && (
          <div className="px-3 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-600">
            <StandardPagination
              currentPage={pageClamped}
              totalPages={totalPagesStable}
              totalItems={totalItemsStable}
              pageSize={clientsPageSize}
              onPageChange={setClientsPage}
              showPageSizeSelector={false}
              showItemCount={true}
            />
          </div>
        )}
      </div>
    </div>
    );
  };

  const renderCreditBlocksTab = () => {
    const allRows = creditBlocksTabRows
      .map((sub) => ({
        id: String(sub.subscriptionLabel ?? sub.name ?? sub.id ?? '').trim() || String(sub.id ?? ''),
        subscriptionId: String(sub.id ?? '').trim(),
        clientLookupId: String(sub.clientLookupId ?? '').trim(),
        subscriber: sub.clientName ?? sub.clientLookupId ?? '—',
        startDate: sub.enrollmentDate,
        endDate: sub.endDate,
        activeCasesOpen: Number(sub.activeCasesOpen ?? 0),
      }))
      .sort((a, b) => {
        return a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' });
      });

    // Client-side search filter against the already-loaded chunk-walked rows.
    // No extra API calls — just filters the local array.
    const searchTerm = creditBlocksSearchQuery.trim().toLowerCase();
    const rows = searchTerm
      ? allRows.filter((row) =>
          row.id.toLowerCase().includes(searchTerm) ||
          row.subscriber.toLowerCase().includes(searchTerm) ||
          row.clientLookupId.toLowerCase().includes(searchTerm) ||
          row.subscriptionId.toLowerCase().includes(searchTerm),
        )
      : allRows;

    const formatDate = (value?: string | number) => {
      if (value == null || value === '') return '—';

      const raw = String(value).trim();
      if (!raw) return '—';

      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
        const [year, month, day] = raw.split('-').map(Number);
        const localDate = new Date(year, month - 1, day);
        return Number.isNaN(localDate.getTime()) ? raw : localDate.toLocaleDateString();
      }

      let d = new Date(raw);

      // Support Unix timestamps from production data in either seconds or milliseconds.
      if (Number.isNaN(d.getTime()) && /^\d+$/.test(raw)) {
        const numeric = Number(raw);
        if (!Number.isNaN(numeric)) {
          const epochMs = raw.length <= 10 ? numeric * 1000 : numeric;
          d = new Date(epochMs);
        }
      }

      return Number.isNaN(d.getTime()) ? raw : d.toLocaleDateString();
    };
    const openSubscriptionWorkspace = (row: { clientLookupId: string; subscriptionId: string }) => {
      const clientLookup = toClientGobodoObjectKey(row.clientLookupId) || row.clientLookupId;
      if (!clientLookup || !row.subscriptionId) return;
      setSelectedClient(null);
      setCurrentView('client-detail');
      navigate(
        `/credit-block/client/${encodeURIComponent(clientLookup)}/subscription?subscriptionId=${encodeURIComponent(row.subscriptionId)}`,
        { replace: false }
      );
    };

    return (
      <div className="space-y-6">
        <CreditBlockListHeader
          title={creditBlocksStatusFilter === 'active' ? 'Active Subscriptions' : 'All Subscriptions'}
          count={{
            displayed: rows.length,
            total: searchTerm ? undefined : creditBlocksTotalItemsStable,
            label: 'subscription',
            loading: creditBlocksTabLoading,
          }}
          search={{
            value: creditBlocksSearchQuery,
            onChange: setCreditBlocksSearchQuery,
            placeholder: 'Search by subscription, subscriber, or client ID...',
          }}
          filters={
            <StandardSelect
              aria-label="Subscription status"
              value={creditBlocksStatusFilter}
              onChange={(e) => {
                setCreditBlocksStatusFilter(e.target.value as 'active' | 'all');
                setCreditBlocksPage(1);
              }}
              options={[
                { value: 'active', label: 'Active only' },
                { value: 'all', label: 'All (incl. cancelled)' },
              ]}
            />
          }
        />

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
          {creditBlocksTabLoading ? (
            <div className="p-6 animate-pulse">
              <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded mb-4" />
              <div className="space-y-3">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700 rounded" />
                ))}
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No Credit Block subscription records found.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Subscriber
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Start Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    End Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Active Cases Open
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                {rows.map((row) => (
                  <tr key={`${row.id}-${row.subscriber}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-700 dark:text-blue-300">
                      {row.clientLookupId ? (
                        <button
                          type="button"
                          onClick={() => openSubscriptionWorkspace(row)}
                          className="text-blue-700 dark:text-blue-300 hover:underline"
                        >
                          {row.id}
                        </button>
                      ) : (
                        row.id
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {row.subscriber || 'Unknown client'}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(row.startDate)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {formatDate(row.endDate)}
                    </td>
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {row.activeCasesOpen}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!creditBlocksTabLoading && (creditBlocksTotalItemsStable > 0 || rows.length > 0) && (
            <div className="px-3 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-600">
              <StandardPagination
                currentPage={Math.min(creditBlocksPage, creditBlocksTotalPagesStable)}
                totalPages={creditBlocksTotalPagesStable}
                totalItems={creditBlocksTotalItemsStable}
                pageSize={creditBlocksPageSize}
                onPageChange={setCreditBlocksPage}
                onPageSizeChange={handleCreditBlocksPageSizeChange}
                pageSizeOptions={[10, 25, 50, 100]}
                showPageSizeSelector={true}
                showItemCount={true}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderUnlockRequestsTab = () => (
    <div className="space-y-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-950/40">
                  <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Lock / Unlock Requests</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {unlockRequestsTotalCount > 0
                      ? `${unlockRequestsTableRows.length} of ${unlockRequestsTotalCount} requests`
                      : 'Credit lock and unlock requests across all clients'}
                  </p>
                </div>
              </div>
              <StandardSearchBar
                value={unlockRequestsSearchQuery}
                onChange={setUnlockRequestsSearchQuery}
                placeholder="Search by client, bureau, status..."
                className="w-full sm:w-72"
              />
              <StandardButton
                variant="ghost"
                type="button"
                onClick={() =>
                  setUnlockRequestsDateSort((prev) => (prev === 'newest' ? 'oldest' : 'newest'))
                }
                className="shrink-0"
              >
                Requested: {unlockRequestsDateSort === 'newest' ? 'Newest first' : 'Oldest first'}
              </StandardButton>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {([
                { value: 'all', label: 'All' },
                { value: 'lock_request', label: 'Lock' },
                { value: 'unlock_request', label: 'Unlock' },
                { value: 'permanent_block', label: 'Perm. Block' },
                { value: 'permanent_unblock', label: 'Perm. Unblock' },
                { value: 'extension', label: 'Extension' },
                { value: 'special_case', label: 'Special' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRequestTypeFilter(opt.value as typeof requestTypeFilter)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
                    requestTypeFilter === opt.value
                      ? 'bg-blue-600 text-white border-blue-600 dark:bg-blue-500 dark:border-blue-500'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div>
          {unlockRequestsPending ? (
            <div className="p-6 animate-pulse space-y-3" aria-busy="true">
              <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded" />
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700 rounded" />
              ))}
            </div>
          ) : unlockRequestsError ? (
            <div className="mx-6 mb-6 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Couldn&apos;t load requests</h3>
                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-200/90">
                    {unlockRequestsQueryError instanceof Error
                      ? unlockRequestsQueryError.message
                      : 'Unlock request traversal failed. Check your connection and Gobodo configuration, then try again.'}
                  </p>
                </div>
              </div>
            </div>
          ) : unlockRequestsTableRows.length === 0 && unlockRequestsTotalCount === 0 && !unlockRequestsSearchQuery.trim() ? (
            <div className="text-center py-16 px-6">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 mx-auto mb-4">
                <Unlock className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No requests yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                Lock and unlock requests will appear here when cases are created with bureau actions.
              </p>
            </div>
          ) : unlockRequestsTableRows.length === 0 && unlockRequestsSearchQuery.trim() ? (
            <div className="text-center py-12 px-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No requests match "<strong className="text-gray-700 dark:text-gray-300">{unlockRequestsSearchQuery}</strong>"
              </p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto w-full">
              <table className="w-full min-w-[920px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/60">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Bureau</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Requested</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">End Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {unlockRequestsTableRows.map((req, rowIndex) => {
                    const rawCid = String(req.clientId ?? '').trim();
                    const cid =
                      rawCid &&
                      !['-', '—', 'null', 'undefined', 'n/a'].includes(rawCid.toLowerCase())
                        ? rawCid
                        : '';
                    const linkQ = unlockRequestLinkedClientQueries[rowIndex];
                    const linked = linkQ?.data;
                    const nameFromLookup = linked?.displayName?.trim() ?? '';
                    const clientKeyFromLookup = linked?.clientKey?.trim() ?? '';
                    const hasResolvedClient =
                      !!nameFromLookup ||
                      !!req.clientName?.trim() ||
                      !!clientKeyFromLookup ||
                      !!(linked?.clientNumericId ?? '').trim();
                    const clientLabel =
                      nameFromLookup ||
                      (req.clientName && String(req.clientName).trim()) ||
                      accountIdToName.get(cid) ||
                      accountIdToName.get(stripSuffix(cid)) ||
                      cid ||
                      '—';
                    const unresolvedClientLabel =
                      !hasResolvedClient ||
                      clientLabel === '-' ||
                      clientLabel === '—' ||
                      clientLabel.toLowerCase() === 'null' ||
                      clientLabel.toLowerCase() === 'undefined';
                    const nameLookupPending =
                      !!cid &&
                      !hasResolvedClient &&
                      (linkQ?.fetchStatus === 'fetching' || linkQ?.isFetching === true);
                    const clientNumericFromLookup = (linked?.clientNumericId ?? '').trim();
                    const subtitleParts = [
                      clientKeyFromLookup,
                      clientNumericFromLookup && clientNumericFromLookup !== clientKeyFromLookup
                        ? clientNumericFromLookup
                        : '',
                    ].filter(Boolean);
                    const subtitleFromLookup = subtitleParts.join(' · ');
                    const subtitleText =
                      subtitleFromLookup ||
                      (cid && clientLabel !== cid ? cid : '');
                    const expectsEndDate =
                      req.type === 'unlock_request' ||
                      req.type === 'permanent_unblock' ||
                      req.type === 'extension';
                    return (
                    <tr
                      key={req.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => {
                        const key = clientKeyFromLookup || cid;
                        if (key) {
                          void openCreditBlockClient(key);
                        } else {
                          setSelectedRequest({
                            ...req,
                            clientName: nameFromLookup || req.clientName,
                            clientId: clientKeyFromLookup || req.clientId,
                          });
                        }
                      }}
                    >
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                          req.type === 'lock_request' || req.type === 'permanent_block'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                            : req.type === 'unlock_request' || req.type === 'permanent_unblock'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                              : req.type === 'extension'
                                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                : req.type === 'special_case'
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {req.type === 'lock_request' || req.type === 'permanent_block' ? (
                            <Lock className="h-3 w-3" />
                          ) : req.type === 'unlock_request' || req.type === 'permanent_unblock' ? (
                            <Unlock className="h-3 w-3" />
                          ) : req.type === 'extension' ? (
                            <Clock className="h-3 w-3" />
                          ) : req.type === 'special_case' ? (
                            <AlertTriangle className="h-3 w-3" />
                          ) : null}
                          {req.type === 'lock_request'
                            ? 'Lock'
                            : req.type === 'unlock_request'
                              ? 'Unlock'
                              : req.type === 'permanent_block'
                                ? 'Permanent'
                                : req.type === 'permanent_unblock'
                                  ? 'Perm. Unblock'
                                  : req.type === 'extension'
                                    ? 'Extension'
                                    : req.type === 'special_case'
                                      ? 'Special Case'
                                      : req.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                        {nameLookupPending ? (
                          <span className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
                            <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" aria-hidden />
                            {cid ? (
                              <span className="truncate font-mono text-xs" title={cid}>
                                {cid}
                              </span>
                            ) : (
                              <span>Loading…</span>
                            )}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5 min-w-0">
                            {unresolvedClientLabel ? (
                              <span className="text-left text-gray-500 dark:text-gray-400 truncate">
                                {clientLabel === '-' ? '—' : clientLabel}
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="text-left text-blue-600 dark:text-blue-400 hover:underline truncate"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const key = clientKeyFromLookup || cid;
                                  if (key) void openCreditBlockClient(key);
                                }}
                              >
                                {clientLabel}
                              </button>
                            )}
                            {subtitleText ? (
                              <span
                                className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate"
                                title={subtitleText}
                              >
                                {subtitleText}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 capitalize">
                          {req.bureau && getBureauLogo(req.bureau) ? (
                            <img src={getBureauLogo(req.bureau)} alt="" className="h-6 w-6 object-contain" aria-hidden />
                          ) : null}
                          {req.bureau ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full capitalize ${
                          req.status === 'pending' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' :
                          req.status === 'approved' || req.status === 'completed' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                          req.status === 'denied' || req.status === 'failed' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {req.status || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                        {req.requestDate
                          ? new Date(req.requestDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                        {req.endDate
                          ? new Date(req.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                          : expectsEndDate
                            ? '—'
                            : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 max-w-[200px]">
                        <span className="line-clamp-2">{req.description ?? '—'}</span>
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </div>
            {(unlockRequestsTotalCount > 0 || unlockRequestsTableRows.length > 0) && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-600">
                <StandardPagination
                  currentPage={Math.min(unlockRequestsPage, Math.max(1, Math.ceil(unlockRequestsTotalCount / unlockRequestsPageSize)))}
                  totalPages={Math.max(1, Math.ceil(unlockRequestsTotalCount / unlockRequestsPageSize))}
                  totalItems={unlockRequestsTotalCount}
                  pageSize={unlockRequestsPageSize}
                  onPageChange={setUnlockRequestsPage}
                  onPageSizeChange={handleUnlockRequestsPageSizeChange}
                  pageSizeOptions={[10, 25, 50, 100]}
                  showPageSizeSelector={true}
                  showItemCount={true}
                />
              </div>
            )}
            </>
          )}
        </div>
      </div>
      {selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedRequest(null)}>
          <div
            className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Request Details
              </h3>
              <button
                onClick={() => setSelectedRequest(null)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 p-1"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</span>
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">
                  {selectedRequest.type === 'lock_request'
                    ? 'Lock / Block'
                    : selectedRequest.type === 'unlock_request'
                      ? 'Unlock'
                      : selectedRequest.type === 'permanent_block'
                        ? 'Permanent block'
                        : selectedRequest.type === 'permanent_unblock'
                          ? 'Permanent Unblock'
                          : selectedRequest.type === 'extension'
                            ? 'Extension'
                            : selectedRequest.type === 'special_case'
                              ? 'Special Case'
                              : selectedRequest.type}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Client</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.clientName ?? selectedRequest.clientId ?? '—'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Bureau</span>
                <div className="mt-1 flex items-center gap-2">
                  {selectedRequest.bureau && getBureauLogo(selectedRequest.bureau) ? (
                    <img src={getBureauLogo(selectedRequest.bureau)} alt="" className="h-8 w-8 object-contain" aria-hidden />
                  ) : null}
                  <span className="text-sm text-gray-900 dark:text-gray-100 capitalize">{selectedRequest.bureau ?? '—'}</span>
                </div>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</span>
                <p className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{selectedRequest.status}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requested By</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.requestedBy || '—'}</p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Request Date</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {selectedRequest.requestDate ? new Date(selectedRequest.requestDate).toLocaleString() : '—'}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Start Date</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {selectedRequest.startDate
                    ? new Date(selectedRequest.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : selectedRequest.type === 'unlock_request' ||
                        selectedRequest.type === 'permanent_unblock' ||
                        selectedRequest.type === 'extension'
                      ? '—'
                      : 'N/A'}
                </p>
              </div>
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">End Date</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">
                  {selectedRequest.endDate
                    ? new Date(selectedRequest.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                    : selectedRequest.type === 'unlock_request' ||
                        selectedRequest.type === 'permanent_unblock' ||
                        selectedRequest.type === 'extension'
                      ? '—'
                      : 'N/A'}
                </p>
              </div>
              {selectedRequest.processedBy && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Processed By</span>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.processedBy}</p>
                </div>
              )}
              {selectedRequest.processedDate && (
                <div>
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Processed Date</span>
                  <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.processedDate}</p>
                </div>
              )}
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Description</span>
                <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{selectedRequest.description ?? '—'}</p>
              </div>
              {selectedRequest.clientId ? (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-600">
                  <button
                    onClick={() => {
                      const req = selectedRequest;
                      setSelectedRequest(null);
                      if (req?.clientId) void openCreditBlockClient(req.clientId);
                    }}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    View client →
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const casePriorityBadge = (p: string) => {
    const cls = p === 'High' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
      : p === 'Medium' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
      : p === 'Low' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>{p || '—'}</span>;
  };
  const caseStatusBadge = (s: string) => {
    const cls = s === 'New' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
      : s === 'Open' || s === 'In Progress' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300'
      : s === 'Closed' || s === 'Resolved' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      : s === 'Escalated' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
      : s === 'Cancelled' ? 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>{s || '—'}</span>;
  };

  const renderCasesTab = () => (
    <div className="space-y-6">
      <CreditBlockListHeader
        title="Credit Block Cases"
        count={{
          displayed: creditBlockCases.length,
          total: casesTotalCount > 0 ? casesTotalCount : undefined,
          label: 'case',
          loading: casesLoading,
        }}
        search={{
          value: casesSearchQuery,
          onChange: setCasesSearchQuery,
          placeholder: 'Search by subject, status, owner...',
        }}
        primaryAction={
          <StandardButton
            onClick={() => setNewCaseModalOpen(true)}
            variant="primary"
            size="sm"
            className="whitespace-nowrap"
            leftIcon={<Plus className="h-4 w-4" />}
          >
            New Case
          </StandardButton>
        }
      />
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div>
          {casesLoading ? (
            <div className="p-6 animate-pulse space-y-3" aria-busy="true">
              <div className="h-10 bg-gray-200 dark:bg-gray-600 rounded" />
              {[...Array(5)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 dark:bg-gray-700 rounded" />
              ))}
            </div>
          ) : casesError ? (
            <div className="mx-6 mb-6 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-6">
              <div className="flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden />
                <div>
                  <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Couldn&apos;t load cases</h3>
                  <p className="mt-1 text-sm text-amber-800 dark:text-amber-200/90">
                    {casesQueryError instanceof Error
                      ? casesQueryError.message
                      : 'Case traversal failed. Check your connection and Gobodo configuration, then try again.'}
                  </p>
                </div>
              </div>
            </div>
          ) : creditBlockCases.length === 0 && casesTotalCount === 0 && !casesSearchQuery.trim() ? (
            <div className="text-center py-16 px-6">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-100 dark:bg-gray-700 mx-auto mb-4">
                <Briefcase className="w-7 h-7 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No cases yet</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                Create a case from a client detail page.
              </p>
            </div>
          ) : creditBlockCases.length === 0 && casesSearchQuery.trim() ? (
            <div className="text-center py-12 px-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No cases match "<strong className="text-gray-700 dark:text-gray-300">{casesSearchQuery}</strong>"
              </p>
            </div>
          ) : (
            <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-800/60">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Case #</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Subject</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Client</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Owner</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/50">
                  {creditBlockCases.map((c, rowIndex) => {
                    const rawAid = String(c.accountId ?? '').trim();
                    const aid =
                      rawAid &&
                      !['-', '—', 'null', 'undefined', 'n/a'].includes(rawAid.toLowerCase())
                        ? rawAid
                        : '';
                    const linkQ = caseLinkedClientQueries[rowIndex];
                    const linked = linkQ?.data;
                    const nameFromTraversal = linked?.displayName?.trim() ?? '';
                    const clientKeyFromTraversal = linked?.clientKey?.trim() ?? '';
                    const hasResolvedClient =
                      !!nameFromTraversal ||
                      !!c.accountName?.trim() ||
                      !!clientKeyFromTraversal ||
                      !!(linked?.clientNumericId ?? '').trim();
                    const clientLabel =
                      nameFromTraversal ||
                      (c.accountName && String(c.accountName).trim()) ||
                      accountIdToName.get(aid) ||
                      accountIdToName.get(stripSuffix(aid)) ||
                      aid ||
                      '—';
                    const clientNamePending =
                      !!aid &&
                      !hasResolvedClient &&
                      (linkQ?.fetchStatus === 'fetching' || linkQ?.isFetching === true);
                    const clientNumericFromTraversal = (linked?.clientNumericId ?? '').trim();
                    const subtitleParts = [
                      clientKeyFromTraversal,
                      clientNumericFromTraversal && clientNumericFromTraversal !== clientKeyFromTraversal
                        ? clientNumericFromTraversal
                        : '',
                    ].filter(Boolean);
                    const subtitleFromTraversal = subtitleParts.join(' · ');
                    const subtitleText =
                      subtitleFromTraversal ||
                      (aid && clientLabel !== aid ? aid : '');
                    return (
                    <tr
                      key={c.id}
                      onClick={() => handleCaseSelect(c)}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                    >
                      <td className="px-4 py-3 text-sm font-mono text-gray-600 dark:text-gray-400">
                        {String(c.caseNumber ?? '').trim() || String((c as { numericObjectId?: unknown }).numericObjectId ?? c.id ?? '').trim() || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{c.subject ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 min-w-[200px]">
                        {clientNamePending ? (
                          <span className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 min-w-0">
                            <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" aria-hidden />
                            {aid ? (
                              <span className="truncate font-mono text-xs" title={aid}>
                                {aid}
                              </span>
                            ) : (
                              <span>Loading…</span>
                            )}
                          </span>
                        ) : (
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-gray-900 dark:text-gray-100 truncate">{clientLabel}</span>
                            {subtitleText ? (
                              <span
                                className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate"
                                title={subtitleText}
                              >
                                {subtitleText}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">{caseStatusBadge(String(c.status ?? ''))}</td>
                      <td className="px-4 py-3">{casePriorityBadge(String(c.priority ?? ''))}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300" title={ownerCellTitle(c.owner as string)}>
                        {displayOwner(c.owner as string)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCaseSelect(c);
                          }}
                          className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                        >
                          View
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            {(casesTotalCount > 0 || creditBlockCases.length > 0) && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-600">
                <StandardPagination
                  currentPage={Math.min(casesPage, Math.max(1, Math.ceil(casesTotalCount / casesPageSize)))}
                  totalPages={Math.max(1, Math.ceil(casesTotalCount / casesPageSize))}
                  totalItems={casesTotalCount}
                  pageSize={casesPageSize}
                  onPageChange={setCasesPage}
                  onPageSizeChange={handleCasesPageSizeChange}
                  pageSizeOptions={[10, 25, 50, 100]}
                  showPageSizeSelector={true}
                  showItemCount={true}
                />
              </div>
            )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  const renderCreditReportsTab = () => {
    const reports = allCreditReports ?? [];
    const reportHydrationPending = reportHydrationQueries.some((q) => q.isLoading || q.isFetching);
    const formatDate = (d: string) => {
      if (!d) return '-';
      try { return new Date(d).toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }); } catch { return d.slice(0, 10); }
    };

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Credit Reports</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {reportsTotalItemsStable} item{reportsTotalItemsStable === 1 ? '' : 's'}
              {reportHydrationPending ? ' • Hydrating names…' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StandardSearchBar
              value=""
              onChange={() => {}}
              placeholder="Search this list..."
              className="w-48"
            />
          </div>
        </div>

        {reportsLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Loading credit reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className="p-12 text-center text-gray-500">No credit reports found.</div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Report Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Report Pull Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product Code</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Files</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {reports.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => {
                    if (r.clientId) void openCreditBlockClient(r.clientId);
                  }}>
                    <td className="px-4 py-3 text-sm text-blue-600 hover:underline font-medium">
                      {r.alias || r.id}
                    </td>
                    <td className="px-4 py-3 text-sm text-blue-600 hover:underline" onClick={(e) => {
                      if (r.clientId) { e.stopPropagation(); void openCreditBlockClient(r.clientId); }
                    }}>
                      {r.clientName || (
                        <span className="inline-flex items-center gap-2 text-gray-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                          Loading...
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{formatDate(r.pullDate)}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{r.productCode || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{r.systemInfo?.totalFilesAttached ?? 0}</td>
                    <td className="px-4 py-3 text-sm text-blue-600">
                      {r.systemInfo?.createdBy || (
                        <span className="inline-flex items-center gap-2 text-gray-500">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                          Loading...
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-gray-200">
            <StandardPagination
              currentPage={Math.min(reportsPage, reportsTotalPagesStable)}
              totalPages={reportsTotalPagesStable}
              totalItems={reportsTotalItemsStable}
              pageSize={reportsPageSize}
              onPageChange={setReportsPage}
              onPageSizeChange={handleReportsPageSizeChange}
              pageSizeOptions={[10, 25, 50, 100]}
              showPageSizeSelector={true}
              showItemCount={true}
            />
          </div>
          </>
        )}
      </div>
    );
  };

  const renderPlaceholder = (title: string, description: string) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
        <Settings className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
      <p className="text-gray-600 dark:text-gray-400 mb-4">{description}</p>
      <div className="flex items-center justify-center space-x-2 text-blue-600 dark:text-blue-400">
        <MessageSquare className="h-4 w-4" />
        <span className="text-sm">Ask the Credit Block Assistant for help with this feature</span>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (selectedTab) {
      case 'overview':
        return renderOverview();
      case 'clients':
        return renderClientsTab();
      case 'credit-blocks':
        return renderCreditBlocksTab();
      case 'cases':
        return renderCasesTab();
      case 'unlock-requests':
        return renderUnlockRequestsTab();
      case 'alerts':
        return renderPlaceholder('Alert Center', 'Monitor and manage security alerts, registration failures, and bureau notifications.');
      case 'reports':
        return renderCreditReportsTab();
      case 'settings':
        return renderPlaceholder('Settings', 'Configure notification preferences, monitoring schedules, and security settings.');
      default:
        return renderOverview();
    }
  };

  return wrap(
    <>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {(overviewError || unlockRequestsError) && !opsErrorDismissed && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-red-800 dark:text-red-300">
                  {getErrorStatus(overviewErrorDetail) === 504
                    ? 'Request timed out (504). The API may be overloaded. Try refreshing.'
                    : 'Unable to load data. Refresh the page.'}
                </p>
              </div>
              <button
                onClick={() => setOpsErrorDismissed(true)}
                className="p-1 hover:bg-red-100 dark:hover:bg-red-800 rounded transition-colors"
                title="Dismiss"
              >
                <X className="w-4 h-4 text-red-600 dark:text-red-400" />
              </button>
            </div>
          )}

          {/* Tab Navigation — Add client lives inline with the tab row to remove the prior dead space.
              Pull credit report button removed at this level (CB-003); it stays available on the client detail page. */}
          <div className="mb-8">
            <div className="border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
              <nav className="flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm ${
                        selectedTab === tab.id
                          ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
              {isSystemAdmin && (
                <button
                  type="button"
                  onClick={() => setQuickAddClientOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg shadow-sm hover:from-blue-700 hover:to-blue-600 active:scale-[0.98] transition-all mb-2"
                >
                  <Plus className="h-4 w-4" />
                  Add client
                </button>
              )}
            </div>
          </div>

          {/* Content */}
          {currentView !== 'operations-dashboard' ? renderDetailView() : renderTabContent()}
        </div>
      </div>

      {/* Chatbot */}
      <InternalChatbot
        applicationContext="credit_block"
        position="bottom-right"
      />

      <CreditBlockNewCaseModal
        isOpen={newCaseModalOpen}
        onClose={() => setNewCaseModalOpen(false)}
        extraClients={newCaseModalExtraClients}
        onCreated={(caseId) => {
          setSelectedCaseId(caseId);
          setSelectedCaseRecord(null);
          setCurrentView('case-detail');
        }}
      />

      <CreditBlockQuickAddClientModal
        isOpen={quickAddClientOpen}
        onClose={() => setQuickAddClientOpen(false)}
        onCreated={() => {
          setSelectedTab('clients');
          setCurrentView('operations-dashboard');
          setSearchQuery('');
          setClientsPage(1);
        }}
      />

      {creditPullOpen && (
        creditPullClientId ? (
          <CreditReportOrderWidget
            isOpen
            onClose={() => { setCreditPullOpen(false); setCreditPullClientId(''); setCreditPullClientName(''); }}
            clientId={creditPullClientId}
            clientName={creditPullClientName}
            orderedBy={userName}
            orderedFrom="credit_block"
            onSubmit={async (order: CreditReportOrder) => {
              await creditReportService.orderCreditReport(order);
              setCreditPullOpen(false);
              setCreditPullClientId('');
              setCreditPullClientName('');
            }}
          />
        ) : (
          <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50" onClick={() => setCreditPullOpen(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-950/40">
                    <FileSearch className="h-4.5 w-4.5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Pull Credit Report</h2>
                </div>
                <button type="button" onClick={() => setCreditPullOpen(false)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-5 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">Search for a client to pull their credit report.</p>
                <div className="space-y-2">
                  {(() => {
                    const pickerClients = (newCaseModalExtraClients.length > 0
                      ? newCaseModalExtraClients
                      : paginatedResult?.clients ?? []);
                    if (newCaseModalClientsLoading) {
                      return <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">Loading clients...</p>;
                    }
                    if (pickerClients.length === 0) {
                      return (
                        <p className="text-sm text-gray-500 dark:text-gray-400 py-4 text-center">
                          No clients available for credit pull.
                        </p>
                      );
                    }
                    return (
                    <>
                      <input
                        type="text"
                        placeholder="Type to filter clients..."
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 text-sm"
                        id="credit-pull-search"
                        autoFocus
                        onChange={(e) => {
                          const el = document.getElementById('credit-pull-results');
                          if (el) el.dataset.filter = e.target.value.toLowerCase();
                          el?.querySelectorAll('[data-client-name]').forEach((btn) => {
                            const name = (btn as HTMLElement).dataset.clientName?.toLowerCase() ?? '';
                            (btn as HTMLElement).style.display = name.includes(e.target.value.toLowerCase()) ? '' : 'none';
                          });
                        }}
                      />
                      <div id="credit-pull-results" className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg divide-y divide-gray-100 dark:divide-gray-700">
                        {pickerClients.map((c) => {
                          const cid = String(c.id ?? '').trim();
                          const name = String(c.accountName ?? '').trim() || cid;
                          return (
                            <button
                              key={cid}
                              type="button"
                              data-client-name={name.toLowerCase()}
                              onClick={() => { setCreditPullClientId(cid); setCreditPullClientName(name); }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{name}</p>
                              {c.contact?.email && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">{c.contact.email}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
};

export default CreditBlockApp;
